import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCustomers, type Customer, type CreateCustomerData } from '@/hooks/useCustomers';
import { useToast } from '@/hooks/use-toast';

const createCustomerSchema = z.object({
  name: z.string().min(1, 'Customer name is required'),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  phone: z.string().optional(),
  status: z.enum(['active', 'inactive', 'suspended']).default('active'),
});

type CreateCustomerForm = z.infer<typeof createCustomerSchema>;

interface CreateCustomerDialogProps {
  onCustomerCreated: (customer: Customer) => void;
  onCancel: () => void;
}

export const CreateCustomerDialog = ({ onCustomerCreated, onCancel }: CreateCustomerDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createCustomer } = useCustomers();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateCustomerForm>({
    resolver: zodResolver(createCustomerSchema),
    defaultValues: {
      status: 'active',
    },
  });

  const onSubmit = async (data: CreateCustomerForm) => {
    setIsSubmitting(true);
    try {
      const customerData: CreateCustomerData = {
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        status: data.status,
      };

      await new Promise<void>((resolve, reject) => {
        createCustomer(customerData, {
          onSuccess: (newCustomer: any) => {
            toast({
              title: "Success",
              description: "Customer created successfully",
            });
            onCustomerCreated(newCustomer as Customer);
            resolve();
          },
          onError: (error) => {
            toast({
              title: "Error",
              description: error?.message || "Failed to create customer",
              variant: "destructive",
            });
            reject(error);
          },
        });
      });
    } catch (error) {
      console.error('Failed to create customer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Customer Name *</Label>
        <Input
          id="name"
          {...register('name')}
          placeholder="Enter customer name"
          disabled={isSubmitting}
        />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          {...register('email')}
          placeholder="Enter email address"
          disabled={isSubmitting}
        />
        {errors.email && (
          <p className="text-sm text-destructive">{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone</Label>
        <Input
          id="phone"
          {...register('phone')}
          placeholder="Enter phone number"
          disabled={isSubmitting}
        />
        {errors.phone && (
          <p className="text-sm text-destructive">{errors.phone.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="status">Status</Label>
        <Select
          value={watch('status')}
          onValueChange={(value) => setValue('status', value as 'active' | 'inactive' | 'suspended')}
          disabled={isSubmitting}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
        {errors.status && (
          <p className="text-sm text-destructive">{errors.status.message}</p>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creating...' : 'Create Customer'}
        </Button>
      </div>
    </form>
  );
};